/**
 * Web Push (VAPID) helpers for Selah Slate.
 * All calls use the axios interceptor's Authorization: Bearer <token>.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    window.isSecureContext
  );
}

export async function getExistingSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function fetchPushStatus() {
  try {
    const { data } = await axios.get(`${API_URL}/api/push/status`);
    return !!data?.subscribed;
  } catch {
    return false;
  }
}

/** Full enable flow: must be called from a user gesture. */
export async function enablePush() {
  if (!isPushSupported()) throw new Error('Web Push not supported in this browser');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    const e = new Error('Notification permission denied');
    e.code = 'permission-denied';
    throw e;
  }
  const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  // Ensure the SW is ready before subscribing
  await navigator.serviceWorker.ready;
  const { data: keyResp } = await axios.get(`${API_URL}/api/push/public-key`);
  const applicationServerKey = urlBase64ToUint8Array(keyResp.public_key);

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }
  const raw = sub.toJSON();
  await axios.post(`${API_URL}/api/push/subscribe`, {
    endpoint: raw.endpoint,
    keys: raw.keys,
    user_agent: navigator.userAgent,
  });
  return sub;
}

export async function sendTestPush() {
  const { data } = await axios.post(`${API_URL}/api/push/test`, {});
  return data;
}

export async function getPushPreferences() {
  const { data } = await axios.get(`${API_URL}/api/push/preferences`);
  return data;
}

export async function updatePushPreferences(prefs) {
  const { data } = await axios.put(`${API_URL}/api/push/preferences`, prefs);
  return data;
}

export async function previewWeeklyDigest() {
  const { data } = await axios.post(`${API_URL}/api/push/preview-digest`, {});
  return data;
}

export async function disablePush() {
  const sub = await getExistingSubscription();
  if (sub) {
    try {
      await axios.delete(`${API_URL}/api/push/subscribe`, { params: { endpoint: sub.endpoint } });
    } catch {
      /* ignore server errors on disable */
    }
    try {
      await sub.unsubscribe();
    } catch {
      /* ignore */
    }
  }
  return { ok: true };
}
